import { gql } from "@apollo/client";
import { useCallback } from "react";
import { useMutation } from "@apollo/client/react";

import type {
  UserEditResp,
  UserEditVariables,
  User
} from "../../types/userTypes";

const UPDATE_USER = gql`
  mutation UpdateUser($attributes: UpdateUserInput!) {
    updateUser(attributes: $attributes) {
      user {
        id
        firstName
        lastName
        img {
          small
          medium
        }
      }
      errors
    }
  }
`   

type UseUpdateUserOptions = {
  onCompleted?: (user: User) => void;
  onError?: (error: unknown) => void;
  mutationOptions?: Parameters<typeof useMutation<UserEditResp, UserEditVariables>>[1];
};

const useUpdateUser = (options?: UseUpdateUserOptions) => {
  const [mutate, { data, loading, error }] = useMutation<
    UserEditResp,
    UserEditVariables
  >(UPDATE_USER, options?.mutationOptions);

  const updateUser = useCallback(
    async (input: UserEditVariables['attributes']) => {
      try {
        const result = await mutate({
          variables: {
            attributes: {
              id: input.id!,
              firstName: input.firstName,
              lastName: input.lastName,
              image: input.image,
            },
          },
        });

        const updateUser = result?.data?.updateUser;

        if (updateUser?.errors?.length) {
          throw new Error(updateUser.errors.join(", "));
        }

        if (options?.onCompleted && updateUser?.user) {
          options.onCompleted(updateUser.user);
        }

        return updateUser?.user;
      } catch (err) {
        if (options?.onError) {
          options.onError(err);
        }
        throw err;
      }
    },
    [mutate, options]
  );

  const user = data?.updateUser?.user;

  return {
    updateUser,
    updatedUser: user,
    loading,
    error: error as Error | null | undefined,
  };
}

export default useUpdateUser
